import { Fragment, useMemo } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import {
  buildSweepstakesRules,
  SweepstakesRuleParagraph,
  SweepstakesRulesInput,
  SweepstakesRulesModel,
} from "@simplyk/common";

import { FormTextButton } from "@/forms-design-system/FormTextButton/FormTextButton";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ChevronDown, ChevronUp } from "@/icons/outlined";

import { useSweepstakesRulesContext } from "./Context/SweepstakesRulesContext";

interface SweepstakesRulesProps {
  input: SweepstakesRulesInput;
  onOpenFreeEntry: () => void;
}

interface SweepstakesRulesContentProps {
  model: SweepstakesRulesModel;
  sponsorLegalName: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onOpenFreeEntry: () => void;
}

const COLLAPSED_RULES_HEIGHT = 240;
const COLLAPSED_RULES_FADE_HEIGHT = 160;

const RuleParagraph = ({
  paragraph,
  onOpenFreeEntry,
}: {
  paragraph: SweepstakesRuleParagraph;
  onOpenFreeEntry: () => void;
}) => (
  <FormTypography variant="body2" vibe="moderate" sx={{ whiteSpace: "pre-line" }}>
    {paragraph.segments.map((segment, index) => {
      if (segment.type === "freeEntryLink") {
        return (
          <FormTextButton
            type="button"
            key={index}
            onClick={onOpenFreeEntry}
            sx={{
              color: "inherit",
              display: "inline-flex",
              font: "inherit",
              fontWeight: 700,
              verticalAlign: "baseline",
            }}
          >
            {segment.text}
          </FormTextButton>
        );
      }

      if (segment.type === "strong") {
        return (
          <Box component="strong" key={index} sx={{ fontWeight: 700 }}>
            {segment.text}
          </Box>
        );
      }

      if (segment.type === "externalLink") {
        return (
          <Box
            component="a"
            key={index}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "inherit", display: "inline-flex", font: "inherit", verticalAlign: "baseline" }}
          >
            {segment.text}
          </Box>
        );
      }

      return <Fragment key={index}>{segment.text}</Fragment>;
    })}
  </FormTypography>
);

export const SweepstakesRulesContent = ({
  model,
  sponsorLegalName,
  expanded,
  onExpandedChange,
  onOpenFreeEntry,
}: SweepstakesRulesContentProps) => {
  const { i18n, t } = useTranslate();
  const theme = useTheme();
  const isApprovedEnglishFallback = !i18n.language.toLowerCase().startsWith("en");

  return (
    <Stack gap={1} data-test="sweepstakes-official-rules">
      <FormTypography variant="body2" vibe="quiet">
        {t("ticketing", "sweepstakes.officialRules", { sponsorLegalName })}
      </FormTypography>
      <Box
        position="relative"
        sx={expanded ? undefined : { maxHeight: COLLAPSED_RULES_HEIGHT, overflow: "hidden" }}
        data-test="sweepstakes-rules-copy"
      >
        <Stack gap={1}>
          {isApprovedEnglishFallback && (
            <FormTypography variant="body2" vibe="moderate">
              {t("ticketing", "sweepstakes.englishRulesNotice")}
            </FormTypography>
          )}
          <FormTypography variant="body2" vibe="intense" sx={{ fontWeight: 700 }}>
            {model.disclosure}
          </FormTypography>
          {model.introduction.map((paragraph, index) => (
            <RuleParagraph key={index} paragraph={paragraph} onOpenFreeEntry={onOpenFreeEntry} />
          ))}
        </Stack>
        <Stack gap={3} sx={{ marginTop: 3 }}>
          {model.sections.map((section) => (
            <Stack key={section.id} id={`sweepstakes-rules-${section.id}`} gap={1}>
              <FormTypography variant="subtitle1" vibe="intense">
                {section.title}
              </FormTypography>
              {section.paragraphs.map((paragraph, index) => (
                <RuleParagraph key={index} paragraph={paragraph} onOpenFreeEntry={onOpenFreeEntry} />
              ))}
            </Stack>
          ))}
        </Stack>
        {!expanded && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: COLLAPSED_RULES_FADE_HEIGHT,
              background: `linear-gradient(to bottom, transparent, ${theme.palette.background.default} 100%)`,
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
      <FormTextButton
        type="button"
        onClick={() => onExpandedChange(!expanded)}
        endIcon={expanded ? <ChevronUp /> : <ChevronDown />}
        aria-expanded={expanded}
        sx={{ alignSelf: "flex-start" }}
        data-test="sweepstakes-rules-toggle"
      >
        {t("ticketing", expanded ? "sweepstakes.closeFullRules" : "sweepstakes.seeFullRules")}
      </FormTextButton>
    </Stack>
  );
};

export const SweepstakesRules = ({ input, onOpenFreeEntry }: SweepstakesRulesProps) => {
  const { expanded, setExpanded } = useSweepstakesRulesContext();
  const model = useMemo(() => buildSweepstakesRules(input), [input]);

  return (
    <SweepstakesRulesContent
      model={model}
      sponsorLegalName={input.sponsorLegalName ?? ""}
      expanded={expanded}
      onExpandedChange={setExpanded}
      onOpenFreeEntry={onOpenFreeEntry}
    />
  );
};
